/**
 * Report Engine — pure data processing, no Office.js dependency.
 *
 * Reads flat arrays (from Excel tables) and produces hierarchical report
 * structures that mirror the backend FinancialReportEngineNew.cs logic.
 */

import { CONFIG } from '../config/constants.js';

const MONTH_NAMES = CONFIG.DATE.MONTH_NAMES;

// ── Section enum value constants ─────────────────────────────────────────────

const IncomeSection = {
  None: 'None',
  Revenue: 'Revenue',
  Cogs: 'Cogs',
  OtherOperatingExpense: 'OtherOperatingExpense',
  OtherIncome: 'OtherIncome',
  OtherExpense: 'OtherExpense',
  IncomeTax: 'IncomeTax',
  Depreciation: 'Depreciation',
  ImpairmentLosses: 'ImpairmentLosses',
  FinancialIncome: 'FinancialIncome',
  FinancialExpenses: 'FinancialExpenses',
  OtherRevenue: 'OtherRevenue',
  SalaryExpense: 'SalaryExpense',
  MarketingExpense: 'MarketingExpense',
  IntangibleAssets: 'IntangibleAssets',
  FixedAssets: 'FixedAssets',
  CurrentAssets: 'CurrentAssets',
  Equity: 'Equity',
  LongTermDebt: 'LongTermDebt',
  ShortTermDebt: 'ShortTermDebt',
};

const CashFlowClass = {
  None: 'None',
  Operating: 'Operating',
  Investing: 'Investing',
  Financing: 'Financing',
  Noncash: 'Noncash',
  Cash: 'Cash',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function toBool(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v === '' || v == null) return false;
  const s = String(v).toLowerCase().trim();
  return s === 'true' || s === '1' || s === 'yes';
}

function toNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function getDivisor(numbersIn) {
  if (numbersIn === 1 || numbersIn === 'Thousands') return 1000;
  if (numbersIn === 2 || numbersIn === 'Millions') return 1000000;
  return 1;
}

function scale(amount, divisor) {
  return divisor === 1 ? amount : amount / divisor;
}

/** Matches FinancialStatement.* string from API / Raw_MasterAccounts.Statement */
function normalizeStatementKey(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '');
}

export function isIncomeStatementStatement(s) {
  const k = normalizeStatementKey(s);
  return k === 'incomestatement' || k === 'profitandloss';
}

export function isBalanceSheetStatement(s) {
  const k = normalizeStatementKey(s);
  return k === 'balancesheet';
}

/**
 * Balance sheet cumulative — raw balanceOut only. Chart NormalBalance / IsContra params ignored for testing.
 * Equity and debt sections use sectionSign −1 in buildBalanceSheet for classic positive RHS.
 */
export function balanceSheetPresentationAmount(normalBalance, isContra, cumulativeLedgerBalance) {
  void normalBalance;
  void isContra;
  // const credit = String(normalBalance ?? '').toLowerCase().trim() === 'credit';
  // let base = credit ? -raw : raw;
  // if (isContra) base = -base;
  return Number(cumulativeLedgerBalance) || 0;
}

// ── Join & Filter ────────────────────────────────────────────────────────────

/**
 * Builds PartyId → name map from Raw_Parties table rows.
 * Accepts either "Name" (Raw_Parties) or "PartyName" column headers.
 */
export function buildPartyNameLookup(partyRows, partyHeaders) {
  const col = {};
  partyHeaders.forEach((h, i) => { col[h] = i; });
  const idIdx = col['PartyId'];
  const nameIdx = col['Name'] ?? col['PartyName'];
  const lookup = {};
  if (idIdx === undefined || nameIdx === undefined) return lookup;
  for (const row of partyRows) {
    const id = toNum(row[idIdx]);
    const name = String(row[nameIdx] ?? '').trim();
    if (id > 0 && name) lookup[id] = name;
  }
  return lookup;
}

/**
 * Joins trial balance rows with master account metadata by AccountNumber.
 * Party account # / name come only from Raw_TrialBalance (one row per party account per month
 * when the API uses party-account grain). Mapping tables are not merged into one cell.
 * PartyName is filled from Raw_TrialBalance when present; otherwise from partyNameById / partyRows.
 *
 * @param {{ profileId?: number, partyRows?: array, partyHeaders?: array, partyNameById?: object }} [joinOptions]
 *   profileId — only Raw_MasterAccounts rows for this profile are used (matches Razor).
 *   partyRows/partyHeaders or partyNameById — Raw_Parties lookup (GetTrialBalanceFlat returns empty PartyName).
 */
export function joinData(tbRows, tbHeaders, maRows, maHeaders, joinOptions = {}) {
  const tbCol = {};
  tbHeaders.forEach((h, i) => { tbCol[h] = i; });
  const maCol = {};
  maHeaders.forEach((h, i) => { maCol[h] = i; });

  const wantPid = joinOptions.profileId;
  const filterByProfile =
    wantPid != null && wantPid !== '' && Number.isFinite(Number(wantPid));
  const wantProfileNum = filterByProfile ? Number(wantPid) : null;
  const profileCol = maCol['ProfileId'];

  // Build master account lookup by AccountNumber (optionally scoped to one profile)
  const maLookup = {};
  for (const row of maRows) {
    if (filterByProfile && profileCol !== undefined) {
      const p = Number(row[profileCol]);
      if (Number.isFinite(p) && p !== wantProfileNum) continue;
    }
    const accNum = String(row[maCol['AccountNumber']] ?? '').trim();
    if (accNum) {
      maLookup[accNum] = {
        statement: String(row[maCol['Statement']] ?? ''),
        section: String(row[maCol['Section']] ?? 'None'),
        // Chart NormalBalance / IsContra ignored for report testing (matches server: raw amounts).
        normalBalance: 'Debit', // String(row[maCol['NormalBalance']] ?? 'Debit'),
        cashFlowClass: String(row[maCol['CashFlowClass']] ?? 'None'),
        isCash: toBool(row[maCol['IsCash']]),
        isContra: false, // toBool(row[maCol['IsContra']]),
        masterAccountName: String(row[maCol['AccountName']] ?? ''),
      };
    }
  }

  const partyLookup = joinOptions.partyNameById
    ?? (joinOptions.partyRows && joinOptions.partyHeaders
      ? buildPartyNameLookup(joinOptions.partyRows, joinOptions.partyHeaders)
      : {});

  const enriched = [];
  for (const row of tbRows) {
    const accNum = String(row[tbCol['AccountNumber']] ?? '').trim();
    const ma = maLookup[accNum] || {
      statement: '', section: 'None', normalBalance: 'Debit',
      cashFlowClass: 'None', isCash: false, isContra: false, masterAccountName: '',
    };

    const accountClassRaw = String(row[tbCol['AccountClass']] ?? '').trim();
    const statementResolved =
      (ma.statement && String(ma.statement).trim() !== '')
        ? String(ma.statement)
        : accountClassRaw;

    const partyId = toNum(row[tbCol['PartyId']]);
    const hasPartyAcctCols = tbCol['PartyAccountNumber'] !== undefined;
    const partyAccountNumber = hasPartyAcctCols
      ? String(row[tbCol['PartyAccountNumber']] ?? '').trim()
      : '';
    const partyAccountName = tbCol['PartyAccountName'] !== undefined
      ? String(row[tbCol['PartyAccountName']] ?? '').trim()
      : '';

    enriched.push({
      partyId,
      partyName: (() => {
        const fromRow = String(row[tbCol['PartyName']] ?? '').trim();
        if (fromRow) return fromRow;
        return partyLookup[partyId] ?? '';
      })(),
      year: toNum(row[tbCol['Year']]),
      month: toNum(row[tbCol['Month']]),
      accountNumber: accNum,
      accountName: String(row[tbCol['AccountName']] ?? '') || ma.masterAccountName,
      accountClass: String(row[tbCol['AccountClass']] ?? ''),
      currency: String(row[tbCol['Currency']] ?? ''),
      balanceChange: toNum(row[tbCol['BalanceChange']]),
      balanceIn: toNum(row[tbCol['BalanceIn']]),
      balanceOut: toNum(row[tbCol['BalanceOut']]),
      eliminationsApplied: toBool(row[tbCol['EliminationsApplied']]),
      partyAccountNumber,
      partyAccountName,
      // from master account (fallback: Raw_TrialBalance AccountClass = API Statement)
      statement: statementResolved,
      section: ma.section,
      normalBalance: ma.normalBalance,
      cashFlowClass: ma.cashFlowClass,
      isCash: ma.isCash,
      isContra: ma.isContra,
    });
  }
  return enriched;
}

/**
 * Applies year, party, and period filters to enriched rows.
 */
export function filterRows(rows, cfg) {
  let filtered = rows;

  if (cfg.year) {
    filtered = filtered.filter(r => r.year === cfg.year);
  }

  if (cfg.partyIds && cfg.partyIds.length > 0) {
    const idSet = new Set(cfg.partyIds.map(Number));
    filtered = filtered.filter(r => idSet.has(r.partyId));
  }

  if (cfg.period && cfg.period > 0) {
    const months = getMonthsInPeriod(cfg.periodType, cfg.period, cfg.year);
    if (months.length > 0) {
      const monthSet = new Set(months);
      filtered = filtered.filter(r => monthSet.has(r.month));
    }
  }

  return filtered;
}

/**
 * Year + company scope only (no period/month filter). Used for retained earnings YTD
 * so it matches backend CalculateRetainedEarningsByMonthAsync (sums NI from Jan..month).
 */
export function filterRowsYearAndParties(rows, cfg) {
  let f = rows;
  if (cfg.year) f = f.filter((r) => r.year === cfg.year);
  if (cfg.partyIds && cfg.partyIds.length > 0) {
    const idSet = new Set(cfg.partyIds.map(Number));
    f = f.filter((r) => idSet.has(r.partyId));
  }
  return f;
}

/**
 * Returns month numbers (1-12) to display as columns.
 * Replicates CalculateDisplayMonths from the backend.
 */
export function getDisplayMonths(year, periodType, period) {
  const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  let months;

  if (!period || period === 0 || periodType === 12) {
    months = ALL_MONTHS;
  } else if (period > 0) {
    const span = Math.max(1, periodType);
    const startMonth = 1 + (period - 1) * span;
    if (startMonth > 12) {
      months = ALL_MONTHS;
    } else {
      const endMonth = Math.min(startMonth + span - 1, 12);
      months = [];
      for (let m = startMonth; m <= endMonth; m++) months.push(m);
    }
  } else {
    months = ALL_MONTHS;
  }

  // For current year, cap at current month
  const now = new Date();
  if (year === now.getFullYear()) {
    const currentMonth = now.getMonth() + 1;
    months = months.filter(m => m <= currentMonth);
  }

  return months.length > 0 ? months : ALL_MONTHS;
}

function getMonthsInPeriod(periodType, period, year) {
  if (!period || period <= 0) return [];
  const span = Math.max(1, periodType || 12);
  const startMonth = (period - 1) * span + 1;
  const endMonth = period * span;
  if (startMonth < 1 || startMonth > 12 || endMonth > 12) return [];

  let months = [];
  for (let m = startMonth; m <= endMonth; m++) months.push(m);

  const now = new Date();
  if (year === now.getFullYear()) {
    months = months.filter(m => m <= now.getMonth() + 1);
  }
  return months;
}

// ── Trial Balance Report ─────────────────────────────────────────────────────

/**
 * Builds a flat trial balance report from enriched rows.
 * Groups by party + master account, with party (local) and master account labels.
 * Columns: January IB (opening balance in), Jan–Dec OB (closing balance out), YTD (period change).
 */
export function buildTrialBalance(filtered, displayMonths, numbersIn) {
  const divisor = getDivisor(numbersIn);

  const byKey = {};
  for (const r of filtered) {
    const pan = String(r.partyAccountNumber ?? '').trim();
    const key = pan
      ? `${r.partyId}|${pan}`
      : `${r.partyId}|${r.accountNumber}`;
    if (!byKey[key]) {
      byKey[key] = {
        partyId: r.partyId,
        partyName: r.partyName,
        partyAccountNumber: r.partyAccountNumber ?? '',
        partyAccountName: r.partyAccountName ?? '',
        accountNumber: r.accountNumber,
        accountName: r.accountName,
        accountClass: r.accountClass,
        statement: r.statement,
        section: r.section,
        monthly: {},
      };
    }
    const acc = byKey[key];
    if (!acc.monthly[r.month]) {
      acc.monthly[r.month] = { change: 0, balIn: 0, balOut: 0 };
    }
    acc.monthly[r.month].change += r.balanceChange;
    acc.monthly[r.month].balIn += r.balanceIn;
    acc.monthly[r.month].balOut += r.balanceOut;
  }

  const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const headers = [
    'Party ID', 'Party Name', 'Party account #', 'Party account name',
    'Master account #', 'Master account name',
  ];
  // Opening balance at start of year (January IB) — matches web Trial Balance when OB is enabled.
  headers.push(`${MONTH_NAMES[0]} IB`);
  // Closing balance (OB) for each month Jan–Dec — BalanceOut from Raw_TrialBalance.
  for (const m of ALL_MONTHS) {
    headers.push(`${MONTH_NAMES[m - 1]} OB`);
  }
  // YTD = sum of period movements (BalanceChange) across displayed months
  headers.push('YTD');

  const rows = [];
  for (const acc of Object.values(byKey)) {
    const row = [
      acc.partyId,
      acc.partyName,
      acc.partyAccountNumber,
      acc.partyAccountName,
      acc.accountNumber,
      acc.accountName,
    ];
    const janBal = acc.monthly[1] || { change: 0, balIn: 0, balOut: 0 };
    row.push(scale(janBal.balIn, divisor));

    let ytd = 0;
    for (const m of ALL_MONTHS) {
      const bal = acc.monthly[m] || { change: 0, balIn: 0, balOut: 0 };
      row.push(scale(bal.balOut, divisor));
      if (displayMonths.includes(m)) {
        ytd += bal.change;
      }
    }
    row.push(scale(ytd, divisor));
    rows.push(row);
  }

  rows.sort((a, b) => {
    const pa = Number(a[0]) || 0;
    const pb = Number(b[0]) || 0;
    if (pa !== pb) return pa - pb;
    const byPartyAcct = String(a[2] ?? '').localeCompare(String(b[2] ?? ''));
    if (byPartyAcct !== 0) return byPartyAcct;
    return String(a[4]).localeCompare(String(b[4]));
  });

  return {
    title: `Trial Balance`,
    headers,
    rows,
    displayMonths,
    labelColumnCount: 6,
  };
}

// ── P&L Report ───────────────────────────────────────────────────────────────

/**
 * Monthly P&L series matching FinancialReportEngineNew CalculatePnLMetrics / BuildPnLSections.
 */
function buildPlMonthlyComputations(sectionGroups, displayMonths) {
  const getSectionMonthlyTotals = (sectionEnum, negate = false) => {
    return _getSectionMonthlyTotals(sectionGroups[sectionEnum] || [], displayMonths, negate);
  };

  const revMonthly = getSectionMonthlyTotals(IncomeSection.Revenue, true);
  const otherRevMonthly = getSectionMonthlyTotals(IncomeSection.OtherRevenue, true);
  const opRevMonthly = addMonthly(revMonthly, otherRevMonthly);

  const cogsMonthly = getSectionMonthlyTotals(IncomeSection.Cogs);
  const grossProfitMonthly = subtractMonthly(opRevMonthly, cogsMonthly);

  const salaryMonthly = getSectionMonthlyTotals(IncomeSection.SalaryExpense);
  const marketMonthly = getSectionMonthlyTotals(IncomeSection.MarketingExpense);
  const opexMonthly = getSectionMonthlyTotals(IncomeSection.OtherOperatingExpense);
  const ebitdaMonthly = subtractMonthly(grossProfitMonthly, addMonthly(salaryMonthly, addMonthly(marketMonthly, opexMonthly)));

  const depMonthly = getSectionMonthlyTotals(IncomeSection.Depreciation);
  const impairMonthly = getSectionMonthlyTotals(IncomeSection.ImpairmentLosses);
  const ebitMonthly = subtractMonthly(ebitdaMonthly, addMonthly(depMonthly, impairMonthly));

  const nonOpIncMonthly = getSectionMonthlyTotals(IncomeSection.OtherIncome, true);
  const nonOpExpMonthly = getSectionMonthlyTotals(IncomeSection.OtherExpense);
  const ebitAfterNonOpMonthly = subtractMonthly(addMonthly(ebitMonthly, nonOpIncMonthly), nonOpExpMonthly);

  const finIncMonthly = getSectionMonthlyTotals(IncomeSection.FinancialIncome, true);
  const finExpMonthly = getSectionMonthlyTotals(IncomeSection.FinancialExpenses);
  const ebtMonthly = subtractMonthly(addMonthly(ebitAfterNonOpMonthly, finIncMonthly), finExpMonthly);

  const taxMonthly = getSectionMonthlyTotals(IncomeSection.IncomeTax);
  const netIncomeMonthly = subtractMonthly(ebtMonthly, taxMonthly);

  return {
    revMonthly,
    otherRevMonthly,
    opRevMonthly,
    cogsMonthly,
    grossProfitMonthly,
    salaryMonthly,
    marketMonthly,
    opexMonthly,
    ebitdaMonthly,
    depMonthly,
    impairMonthly,
    ebitMonthly,
    nonOpIncMonthly,
    nonOpExpMonthly,
    ebitAfterNonOpMonthly,
    finIncMonthly,
    finExpMonthly,
    ebtMonthly,
    taxMonthly,
    netIncomeMonthly,
  };
}

/**
 * Builds a hierarchical Profit & Loss report from enriched rows.
 * Replicates BuildPnLSections from FinancialReportEngineNew.cs.
 */
export function buildProfitLoss(filtered, displayMonths, numbersIn) {
  const divisor = getDivisor(numbersIn);
  const incomeRows = filtered.filter((r) => isIncomeStatementStatement(r.statement));

  // Group by Section
  const sectionGroups = {};
  for (const r of incomeRows) {
    const sec = r.section || 'None';
    if (!sectionGroups[sec]) sectionGroups[sec] = [];
    sectionGroups[sec].push(r);
  }

  const createSection = (sectionEnum, name, property, negate = false) => {
    return _createDataSection(sectionGroups[sectionEnum] || [], name, property, negate, displayMonths, divisor);
  };

  const m = buildPlMonthlyComputations(sectionGroups, displayMonths);
  const {
    opRevMonthly,
    grossProfitMonthly,
    ebitdaMonthly,
    ebitMonthly,
    ebtMonthly,
    netIncomeMonthly,
  } = m;

  const sections = [
    createSection(IncomeSection.Revenue, 'Revenue', 'Revenue', true),
    createSection(IncomeSection.OtherRevenue, 'Other Operating Revenue', 'OtherRevenue', true),
    _createCalcSection('Operating Revenue', 'OperatingRevenue', opRevMonthly, divisor, true),
    createSection(IncomeSection.Cogs, 'COGS', 'Cogs'),
    _createCalcSection('Gross Profit', 'GrossProfit', grossProfitMonthly, divisor, true),
    createSection(IncomeSection.SalaryExpense, 'Salary Expenses', 'SalaryExpenses'),
    createSection(IncomeSection.MarketingExpense, 'Marketing Expenses', 'MarketingExpenses'),
    createSection(IncomeSection.OtherOperatingExpense, 'Other operating expenses', 'OperatingExpenses'),
    _createCalcSection('EBITDA', 'Ebitda', ebitdaMonthly, divisor, true),
    createSection(IncomeSection.Depreciation, 'Depreciation', 'Depreciation'),
    createSection(IncomeSection.ImpairmentLosses, 'Impairment Losses', 'ImpairmentLosses'),
    _createCalcSection('EBIT', 'Ebit', ebitMonthly, divisor, true),
    createSection(IncomeSection.OtherIncome, 'Other income (non-operating)', 'NonOperatingIncome', true),
    createSection(IncomeSection.OtherExpense, 'Other expense (non-operating)', 'NonOperatingExpense'),
    createSection(IncomeSection.FinancialIncome, 'Financial income', 'FinancialIncome', true),
    createSection(IncomeSection.FinancialExpenses, 'Financial expenses', 'FinancialExpenses'),
    _createCalcSection('EBT', 'Ebt', ebtMonthly, divisor, true),
    createSection(IncomeSection.IncomeTax, 'Tax', 'IncomeTax'),
    _createCalcSection('Net Income/(Loss)', 'NetIncome', netIncomeMonthly, divisor, true),
  ];

  return {
    title: 'Profit & Loss',
    sections: sections.filter(s => s.children.length > 0 || s.isCalculated),
    displayMonths,
    showYtd: true,
  };
}

// ── Balance Sheet Report ─────────────────────────────────────────────────────

/**
 * Builds a hierarchical Balance Sheet report.
 * Replicates BuildHierarchicalBalanceSheetFromTrialBalance (amounts + retained earnings).
 * @param {{ year?: number, retainedEarningsRows?: object[] }} [options] — RE uses year-wide P&amp;L rows (same parties), not period-filtered TB.
 */
export function buildBalanceSheet(filtered, displayMonths, numbersIn, options = {}) {
  const divisor = getDivisor(numbersIn);
  const bsRows = filtered.filter((r) => isBalanceSheetStatement(r.statement));

  const sectionGroups = {};
  for (const r of bsRows) {
    const sec = r.section || 'None';
    if (!sectionGroups[sec]) sectionGroups[sec] = [];
    sectionGroups[sec].push(r);
  }

  const createSection = (sectionEnum, name, property, sectionSign = 1) => {
    return _createDataSectionBs(sectionGroups[sectionEnum] || [], name, property, displayMonths, divisor, sectionSign);
  };

  const getSectionMonthlyTotals = (sectionEnum, sectionSign = 1) => {
    return _getSectionMonthlyTotalsBs(sectionGroups[sectionEnum] || [], displayMonths, sectionSign);
  };

  // Asset totals
  const intangibleMonthly = getSectionMonthlyTotals(IncomeSection.IntangibleAssets);
  const fixedMonthly = getSectionMonthlyTotals(IncomeSection.FixedAssets);
  const currentMonthly = getSectionMonthlyTotals(IncomeSection.CurrentAssets);
  const totalAssetsMonthly = addMonthly(intangibleMonthly, addMonthly(fixedMonthly, currentMonthly));

  // Equity + retained earnings
  const equityMonthly = getSectionMonthlyTotals(IncomeSection.Equity, -1);
  const reSource = options.retainedEarningsRows ?? filtered;
  const reYear = options.year ?? filtered[0]?.year;
  const retainedEarningsMonthly = _calcRetainedEarningsByMonth(reSource, displayMonths, reYear);
  const totalEquityMonthly = addMonthly(equityMonthly, retainedEarningsMonthly);

  // Liabilities (credit cumulatives → positive magnitudes, same rule as equity)
  const longTermMonthly = getSectionMonthlyTotals(IncomeSection.LongTermDebt, -1);
  const shortTermMonthly = getSectionMonthlyTotals(IncomeSection.ShortTermDebt, -1);
  const totalLiabilitiesMonthly = addMonthly(longTermMonthly, shortTermMonthly);
  const totalEqAndLiabMonthly = addMonthly(totalEquityMonthly, totalLiabilitiesMonthly);
  const balanceMonthly = totalAssetsMonthly.map((a, i) => a - (totalEqAndLiabMonthly[i] || 0));

  const sections = [
    createSection(IncomeSection.IntangibleAssets, 'Intangible Assets', 'IntangibleAssets'),
    _createCalcSection('Total Intangible Assets', 'TotalIntangibleAssets', intangibleMonthly, divisor, false),
    createSection(IncomeSection.FixedAssets, 'Fixed Assets', 'FixedAssets'),
    _createCalcSection('Total Fixed Assets', 'TotalFixedAssets', fixedMonthly, divisor, false),
    createSection(IncomeSection.CurrentAssets, 'Current Assets', 'CurrentAssets'),
    _createCalcSection('Total Current Assets', 'TotalCurrentAssets', currentMonthly, divisor, false),
    _createCalcSection('Total Assets', 'TotalAssets', totalAssetsMonthly, divisor, true),
    createSection(IncomeSection.Equity, 'Equity', 'Equity', -1),
    _createCalcSection('Retained Earnings', 'RetainedEarnings', retainedEarningsMonthly, divisor, false),
    _createCalcSection('Total Equity', 'TotalEquity', totalEquityMonthly, divisor, true),
    createSection(IncomeSection.LongTermDebt, 'Long-term Debt', 'LongTermDebt', -1),
    _createCalcSection('Total Long-term Debt', 'TotalLongTermDebt', longTermMonthly, divisor, false),
    createSection(IncomeSection.ShortTermDebt, 'Short-term Debt', 'ShortTermDebt', -1),
    _createCalcSection('Total Short-term Debt', 'TotalShortTermDebt', shortTermMonthly, divisor, false),
    _createCalcSection('Total Liabilities', 'TotalLiabilities', totalLiabilitiesMonthly, divisor, true),
    _createCalcSection('Total Equity and Liabilities', 'TotalEquityAndLiabilities', totalEqAndLiabMonthly, divisor, true),
    _createCalcSection('Balance', 'Balance', balanceMonthly, divisor, true),
  ];

  return {
    title: 'Balance Sheet',
    sections,
    displayMonths,
    showYtd: false,
  };
}

// ── Cash Flow Report ─────────────────────────────────────────────────────────

/**
 * Builds a hierarchical Cash Flow report.
 * Mirrors FinancialReportEngineNew: net change in cash = Closing − Opening (cash balances);
 * Operating/Investing/Financing/Noncash/Unclassified are activity detail; may not tie if TB is inconsistent.
 */
export function buildCashFlow(filtered, displayMonths, numbersIn) {
  const divisor = getDivisor(numbersIn);

  // Group all rows by (AccountNumber, Month) and classify by CashFlowClass
  const accountMonthly = {};  // accNum -> { acc metadata, months: { month -> amount } }
  const cashAccounts = {};    // accNum -> { months: { month -> { balIn, balOut } } }

  for (const r of filtered) {
    if (!r.accountNumber) continue;

    if (r.cashFlowClass === CashFlowClass.Cash || r.isCash) {
      if (!cashAccounts[r.accountNumber]) {
        cashAccounts[r.accountNumber] = { months: {} };
      }
      if (!cashAccounts[r.accountNumber].months[r.month]) {
        cashAccounts[r.accountNumber].months[r.month] = { balIn: 0, balOut: 0 };
      }
      cashAccounts[r.accountNumber].months[r.month].balIn += r.balanceIn;
      cashAccounts[r.accountNumber].months[r.month].balOut += r.balanceOut;
      continue;
    }

    // Raw BalanceChange; CF section uses ToCashFlowPresentationAmount (−ledger) like server.
    const ledgerAmount = r.balanceChange;
    const cashFlowAmount = -ledgerAmount; // ToCashFlowPresentationAmount

    if (cashFlowAmount === 0) continue;

    if (!accountMonthly[r.accountNumber]) {
      accountMonthly[r.accountNumber] = {
        accountNumber: r.accountNumber,
        accountName: r.accountName,
        cashFlowClass: r.cashFlowClass || 'None',
        months: {},
      };
    }
    if (!accountMonthly[r.accountNumber].months[r.month]) {
      accountMonthly[r.accountNumber].months[r.month] = 0;
    }
    accountMonthly[r.accountNumber].months[r.month] += cashFlowAmount;
  }

  // Classify into buckets
  const buckets = {
    Operating: [], Investing: [], Financing: [], Noncash: [], Unclassified: [],
  };
  for (const acc of Object.values(accountMonthly)) {
    const bucket = buckets[acc.cashFlowClass] || buckets.Unclassified;
    bucket.push(acc);
  }

  const createCFSection = (items, name, property) => {
    const children = items.map(acc => {
      const monthlyAmounts = displayMonths.map(m => scale(acc.months[m] || 0, divisor));
      const ytd = displayMonths.reduce((sum, m) => sum + (acc.months[m] || 0), 0);
      return {
        name: acc.accountName,
        accountNumber: acc.accountNumber,
        monthlyAmounts,
        ytdAmount: scale(ytd, divisor),
      };
    }).filter(c => c.ytdAmount !== 0 || c.monthlyAmounts.some(v => v !== 0));

    children.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

    const monthlyTotals = displayMonths.map((m, i) => children.reduce((s, c) => s + c.monthlyAmounts[i], 0));
    const ytdTotal = children.reduce((s, c) => s + c.ytdAmount, 0);

    return {
      name, property, isCalculated: false, isBold: false,
      monthlyTotals, ytdTotal, children,
      sectionKey: `section_${property}`,
    };
  };

  const getBucketMonthlyTotals = (items) => {
    return displayMonths.map(m => {
      let sum = 0;
      for (const acc of items) sum += acc.months[m] || 0;
      return scale(sum, divisor);
    });
  };

  const getBucketYtd = (items) => {
    let total = 0;
    for (const acc of items) {
      for (const m of displayMonths) total += acc.months[m] || 0;
    }
    return scale(total, divisor);
  };

  // Opening/closing cash
  const openingMonthly = displayMonths.map(m => {
    let sum = 0;
    for (const ca of Object.values(cashAccounts)) {
      sum += ca.months[m]?.balIn || 0;
    }
    return scale(sum, divisor);
  });
  const closingMonthly = displayMonths.map(m => {
    let sum = 0;
    for (const ca of Object.values(cashAccounts)) {
      sum += ca.months[m]?.balOut || 0;
    }
    return scale(sum, divisor);
  });

  const opMonthly = getBucketMonthlyTotals(buckets.Operating);
  const invMonthly = getBucketMonthlyTotals(buckets.Investing);
  const finMonthly = getBucketMonthlyTotals(buckets.Financing);
  const ncMonthly = getBucketMonthlyTotals(buckets.Noncash);
  const unclMonthly = getBucketMonthlyTotals(buckets.Unclassified);
  // Authoritative net change from cash balances (matches backend Closing − Opening)
  const netChangeMonthly = displayMonths.map((_, i) =>
    closingMonthly[i] - openingMonthly[i]
  );

  const sections = [
    createCFSection(buckets.Operating, 'Cash from Operating Activities', 'Operating'),
    _createCalcSection('Net Cash from Operating Activities', 'TotalOperating', opMonthly, 1, false, getBucketYtd(buckets.Operating)),
    createCFSection(buckets.Investing, 'Cash from Investing Activities', 'Investing'),
    _createCalcSection('Net Cash from Investing Activities', 'TotalInvesting', invMonthly, 1, false, getBucketYtd(buckets.Investing)),
    createCFSection(buckets.Financing, 'Cash from Financing Activities', 'Financing'),
    _createCalcSection('Net Cash from Financing Activities', 'TotalFinancing', finMonthly, 1, false, getBucketYtd(buckets.Financing)),
  ];

  if (buckets.Noncash.length > 0) {
    sections.push(createCFSection(buckets.Noncash, 'Non-Cash Adjustments', 'Noncash'));
    sections.push(_createCalcSection('Total Non-Cash Adjustments', 'TotalNoncash', ncMonthly, 1, false, getBucketYtd(buckets.Noncash)));
  }

  if (buckets.Unclassified.length > 0) {
    sections.push(createCFSection(buckets.Unclassified, 'Unclassified Accounts', 'Unclassified'));
    sections.push(_createCalcSection('Total Unclassified Accounts', 'TotalUnclassified', unclMonthly, 1, false, getBucketYtd(buckets.Unclassified)));
  }

  const netChangeYtd = displayMonths.reduce((s, _, i) => s + netChangeMonthly[i], 0);
  const openingYtd = displayMonths.reduce((s, _, i) => s + openingMonthly[i], 0);
  const closingYtd = displayMonths.reduce((s, _, i) => s + closingMonthly[i], 0);

  sections.push(_createCalcSection('Net Change in Cash', 'NetChangeInCash', netChangeMonthly, 1, true, netChangeYtd));
  sections.push(_createCalcSection('Opening Cash Balance', 'OpeningCash', openingMonthly, 1, false, openingYtd));
  sections.push(_createCalcSection('Closing Cash Balance', 'ClosingCash', closingMonthly, 1, true, closingYtd));

  return {
    title: 'Cash Flow',
    sections,
    displayMonths,
    showYtd: true,
  };
}

// ── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Creates a data section (expandable with child accounts).
 * Groups rows by AccountNumber, sums the specified amount field per month.
 * Sums amountField per account/month; optional negate for revenue-style P&amp;L sections.
 * @param {string} amountField - 'balanceChange' (P&L/CF) or 'balanceOut' (BS cumulative)
 */
function _createDataSection(rows, name, property, negate, displayMonths, divisor, amountField = 'balanceChange') {
  const byAccount = {};
  for (const r of rows) {
    if (!byAccount[r.accountNumber]) {
      byAccount[r.accountNumber] = {
        accountNumber: r.accountNumber,
        accountName: r.accountName,
        months: {},
      };
    }
    const acc = byAccount[r.accountNumber];
    if (!acc.months[r.month]) acc.months[r.month] = 0;
    acc.months[r.month] += r[amountField];
  }

  const children = [];
  for (const acc of Object.values(byAccount)) {
    const monthlyAmounts = displayMonths.map(m => {
      let val = acc.months[m] || 0;
      if (negate) val = -val;
      return scale(val, divisor);
    });
    const rawYtd = displayMonths.reduce((sum, m) => {
      let val = acc.months[m] || 0;
      if (negate) val = -val;
      return sum + val;
    }, 0);

    children.push({
      name: acc.accountName,
      accountNumber: acc.accountNumber,
      monthlyAmounts,
      ytdAmount: scale(rawYtd, divisor),
    });
  }

  children.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

  const monthlyTotals = displayMonths.map((_, i) =>
    children.reduce((sum, c) => sum + c.monthlyAmounts[i], 0)
  );
  const ytdTotal = children.reduce((sum, c) => sum + c.ytdAmount, 0);

  return {
    name, property, isCalculated: false, isBold: false,
    monthlyTotals, ytdTotal, children,
    sectionKey: `section_${property}`,
  };
}

/**
 * Creates a calculated (subtotal) section with no children.
 */
function _createCalcSection(name, property, monthlyTotals, divisor, isBold, ytdOverride) {
  const scaled = divisor === 1 ? monthlyTotals : monthlyTotals.map(v => scale(v, divisor));
  const ytd = ytdOverride !== undefined ? ytdOverride : scaled.reduce((s, v) => s + v, 0);
  return {
    name, property, isCalculated: true, isBold,
    monthlyTotals: scaled, ytdTotal: ytd, children: [],
    sectionKey: '',
  };
}

/**
 * Returns raw (unscaled) monthly totals for a section, used for intermediate calculations.
 * @param {string} amountField - which field to sum: 'balanceChange' or 'balanceOut'
 */
function _getSectionMonthlyTotals(rows, displayMonths, negate, amountField = 'balanceChange') {
  const byAccount = {};
  for (const r of rows) {
    if (!byAccount[r.accountNumber]) {
      byAccount[r.accountNumber] = { months: {} };
    }
    if (!byAccount[r.accountNumber].months[r.month]) {
      byAccount[r.accountNumber].months[r.month] = 0;
    }
    byAccount[r.accountNumber].months[r.month] += r[amountField];
  }

  return displayMonths.map(m => {
    let total = 0;
    for (const acc of Object.values(byAccount)) {
      let val = acc.months[m] || 0;
      if (negate) val = -val;
      total += val;
    }
    return total;
  });
}

function addMonthly(a, b) {
  return a.map((v, i) => v + (b[i] || 0));
}

function subtractMonthly(a, b) {
  return a.map((v, i) => v - (b[i] || 0));
}

/**
 * Mirrors FinancialReportEngineNew.ComputeNetIncomeForMonth (revenue bucket vs expenses) for one month.
 */
function computeNetIncomeForMonthLikeServer(rows, year, month) {
  let monthlyRevenue = 0;
  let monthlyExpenses = 0;
  for (const m of rows) {
    if (m.year !== year || m.month !== month) continue;
    if (!isIncomeStatementStatement(m.statement)) continue;
    const amount = m.balanceChange;
    const sec = String(m.section || '');
    if (
      sec === IncomeSection.Revenue ||
      sec === IncomeSection.OtherRevenue ||
      sec === IncomeSection.FinancialIncome
    ) {
      monthlyRevenue += -amount;
    } else {
      monthlyExpenses += amount;
    }
  }
  return monthlyRevenue - monthlyExpenses;
}

/**
 * Cumulative retained earnings by display month — sum of NI from January through that month
 * (same approach as server CalculateRetainedEarningsByMonthAsync for calendar YTD).
 */
function _calcRetainedEarningsByMonth(yearPartyRows, displayMonths, year) {
  if (year == null || Number.isNaN(Number(year))) {
    return displayMonths.map(() => 0);
  }
  const y = Number(year);
  const sorted = [...displayMonths].sort((a, b) => a - b);
  return sorted.map((targetMonth) => {
    let cumulative = 0;
    for (let m = 1; m <= targetMonth; m++) {
      cumulative += computeNetIncomeForMonthLikeServer(yearPartyRows, y, m);
    }
    return cumulative;
  });
}

function _rowBsLedgerCumulative(r) {
  return balanceSheetPresentationAmount(r.normalBalance, r.isContra, r.balanceOut);
}

/** @param {number} [sectionSign] 1 or −1 (Equity section matches server negation). */
function _createDataSectionBs(rows, name, property, displayMonths, divisor, sectionSign = 1) {
  const byAccount = {};
  for (const r of rows) {
    if (!byAccount[r.accountNumber]) {
      byAccount[r.accountNumber] = {
        accountNumber: r.accountNumber,
        accountName: r.accountName,
        months: {},
      };
    }
    const acc = byAccount[r.accountNumber];
    if (!acc.months[r.month]) acc.months[r.month] = 0;
    acc.months[r.month] += _rowBsLedgerCumulative(r) * sectionSign;
  }

  const children = [];
  for (const acc of Object.values(byAccount)) {
    const monthlyAmounts = displayMonths.map((m) => scale(acc.months[m] || 0, divisor));
    const rawYtd = displayMonths.reduce((sum, m) => sum + (acc.months[m] || 0), 0);
    children.push({
      name: acc.accountName,
      accountNumber: acc.accountNumber,
      monthlyAmounts,
      ytdAmount: scale(rawYtd, divisor),
    });
  }

  children.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

  const monthlyTotals = displayMonths.map((_, i) =>
    children.reduce((sum, c) => sum + c.monthlyAmounts[i], 0)
  );
  const ytdTotal = children.reduce((sum, c) => sum + c.ytdAmount, 0);

  return {
    name,
    property,
    isCalculated: false,
    isBold: false,
    monthlyTotals,
    ytdTotal,
    children,
    sectionKey: `section_${property}`,
  };
}

/** @param {number} [sectionSign] 1 or −1 (Equity section matches server negation). */
function _getSectionMonthlyTotalsBs(rows, displayMonths, sectionSign = 1) {
  const byAccount = {};
  for (const r of rows) {
    if (!byAccount[r.accountNumber]) {
      byAccount[r.accountNumber] = { months: {} };
    }
    if (!byAccount[r.accountNumber].months[r.month]) {
      byAccount[r.accountNumber].months[r.month] = 0;
    }
    byAccount[r.accountNumber].months[r.month] += _rowBsLedgerCumulative(r) * sectionSign;
  }

  return displayMonths.map((m) => {
    let total = 0;
    for (const acc of Object.values(byAccount)) {
      total += acc.months[m] || 0;
    }
    return total;
  });
}
